import React from 'react';
import PropTypes from 'prop-types';

import { withRouter } from 'core/libs/router';

import withBreakpoint from 'core/hocs/withBreakpoint';
import withTheme from 'core/hocs/withTheme';

import Heading from 'core/components/Heading';
import Link from 'core/components/Link';

import ArrowLeft from 'core/components/Icon/ArrowLeft';
import ArrowRight from 'core/components/Icon/ArrowRight';

import themePropTypes from 'core/utils/prop-types/theme';

import { resolveScopedStyles } from 'core/utils/styled-jsx';
import { pageLinkConstructor } from 'core/utils/url-helper';

import styles from './index.styl';


/**
 * @author Aleksey Dibrivniy <a.dibrivniy@rambler-co.ru>
 */
class TopicPages extends React.PureComponent {
  state = {
    minWidth: 0,
  };

  componentDidMount() {
    this.measure();
  }

  componentDidUpdate() {
    this.measure();
  }

  measure() {
    /**
     * Проверка наличия this.sizer для Unit тестов
     */
    const minWidth = this.sizer
      ? this.sizer.getBoundingClientRect().width
      : this.state.minWidth;

    this.setState({ minWidth });
  }

  render() {
    const {
      link,
      active,
      isMobile,
      isDesktop,
      total,
      linkConstructor,
      prevTitle,
      nextTitle,
      location,
      withoutMobileTitles,
      infinity,
      theme: {
        animations: {
          hover: hoverAnimation,
        },
        controls: {
          topicPages: topicPagesAtoms,
          button: buttonAtoms,
        },
      },
    } = this.props;

    const pageLocation = {
      ...location,
      ...link && { pathname: link },
      ...infinity && {
        search: '', hash: '',
      },
    };

    const buttonSize = isDesktop || isMobile && withoutMobileTitles ? 'large' : 'medium';
    const buttonSizeAtoms = buttonAtoms.const.sizes[buttonSize];

    const { minWidth } = this.state;

    /**
     * Хелпер для расчета минимальной ширины заголовка,
     * высчитывается при рендеринге компонента в state.width
     */
    const TextSizer = (
      <div className={styles.sizer}>
        <div
          className={styles.sizerInner}
          ref={instance => (this.sizer = instance)}
        >
          {prevTitle}
          {nextTitle}
        </div>
      </div>
    );

    // eslint-disable-next-line react/prop-types
    const Title = ({ title }) => (
      <div
        className={styles.text}
        style={{ minWidth }}
      >
        <style jsx>{`
          .${styles.text}
            font ${topicPagesAtoms.text.font}
            color ${buttonAtoms.ghost.idle.color}
            transition color ${hoverAnimation}
            &:before
              height ${buttonSizeAtoms.height}
        `}</style>
        <div className={styles.textInner}>
          {title}
        </div>
      </div>
    );

    const scope = resolveScopedStyles(
      <scope>
        <style jsx>{`
          .arrow
            width ${topicPagesAtoms.arrow.size}
            height ${topicPagesAtoms.arrow.size}
          .control
            & :global([id*="$"])
              fill ${topicPagesAtoms.arrow.idle.color}
              transition fill ${hoverAnimation}
            &:hover :global([id*="$"])
              fill ${topicPagesAtoms.arrow.hover.color}
            &:hover :global(.icon)
              border ${buttonAtoms.secondary.hover.border}
              background ${buttonAtoms.secondary.hover.background}
            &:hover :global(.text)
              color ${buttonAtoms.ghost.hover.color}
        `}</style>
      </scope>
    );

    // eslint-disable-next-line react/prop-types
    const Arrow = ({ position }) => (
      <div className={styles.icon}>
        <style jsx>{`
          .${styles.icon}
            width ${buttonSizeAtoms.height}
            height ${buttonSizeAtoms.height}
            border ${buttonAtoms.secondary.idle.border}
            background ${buttonAtoms.secondary.idle.background}
            border-radius ${buttonAtoms.const.radius}
            transition background ${hoverAnimation}
        `}</style>
        {position === 'left'
          ? <ArrowLeft className={scope.wrapClassNames('arrow')} />
          : <ArrowRight className={scope.wrapClassNames('arrow')} />
        }
      </div>
    );

    const hasTitle = (
      isDesktop || isMobile && !withoutMobileTitles
    );

    return (
      <div className={styles.container} data-qa='topic-pages'>
        <style jsx>{`
          .${styles.counter}
            &:before
              height ${buttonSizeAtoms.height}
        `}</style>
        <div className={styles.button}>
          {active > 1 && (
            <Link
              type='secondary'
              className={scope.wrapClassNames('control')}
              to={linkConstructor(Math.max(active - 1, 1), pageLocation)}
            >
              <Arrow position='left' />
              {hasTitle && <Title title={prevTitle} />}
            </Link>
          )}
        </div>

        <div className={styles.counter}>
          <Heading
            className={styles.counterInner}
            level={isDesktop ? 2 : 3}
          >
            {active} из {total}
          </Heading>
        </div>

        {hasTitle && TextSizer}

        <div className={styles.button}>
          {active < total && (
            <Link
              type='secondary'
              className={scope.wrapClassNames('control')}
              to={linkConstructor(Math.min(active + 1, total), pageLocation)}
            >
              <Arrow position='right' />
              {hasTitle && <Title title={nextTitle} />}
            </Link>
          )}
        </div>
        <scope.styles />
      </div>
    );
  }
}

TopicPages.defaultProps = {
  active: 1,
  prevTitle: 'Предыдущая',
  nextTitle: 'Следующая',
  linkConstructor: pageLinkConstructor,
  withoutMobileTitles: false,
};


TopicPages.propTypes = {
  /**
   * Ссылка на топик.
   * В бесконечном скроле ссылка на топик не всегда совпадает с location,
   * если не передана, то используется location.pathname
   */
  link: PropTypes.string,

  /**
   * Подгружен ли топик по доскролу в бесконечном скроле
   */
  infinity: PropTypes.bool,

  /**
   * Номер активной страницы.
   * Не индекс.
   */
  active: PropTypes.number,

  /**
   * Общее количество элементов в пагинации
   */
  total: PropTypes.number.isRequired,

  /**
   * Свойство указывает заголовок следующего шага
   */
  nextTitle: PropTypes.node,

  /**
   * Свойство указывает заголовок предыдущего шага
   */
  prevTitle: PropTypes.node,

  /**
   * Функция, формирует ссылку исходя из активной страницы.
   *
   * @param {number} activeIndex
   * @param {object} location
   */
  linkConstructor: PropTypes.func.isRequired,

  /** @ignore */
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,

  /**
   * Можно отключить рендеринг заголовков в мобильной версии
   */
  withoutMobileTitles: PropTypes.bool,

  /** @ignore */
  isDesktop: PropTypes.bool,

  /** @ignore */
  isMobile: PropTypes.bool,

  /** @ignore */
  theme: themePropTypes(`
    {
      animations: {
        hover,
      },

      controls: {
        button: {
          const: {
            sizes: {
              medium: {
                height,
              },
              large: {
                height,
              },
            },
          },
          secondary: {
            idle: {
              border,
              background,
            },
            hover: {
              border,
              background,
            },
          },
          ghost: {
            idle: {
              color,
            },
            hover: {
              color,
            },
          },
        },

        topicPages: {
          text: {
            font,
            color,
          },

          arrow: {
            size,
          },
        },
      }
    }
  `),
};

const TopicPagesWithHOCs = withRouter(
  withBreakpoint(
    withTheme(TopicPages)
  )
);
TopicPagesWithHOCs.displayName = 'TopicPages';

export { TopicPages as StorybookComponent };
export default TopicPagesWithHOCs;
